import React from 'react';
import styles from './ReceiptsCard.module.scss';
import Typography from '~/shared/components/Typography/Typography';
import type { ReceiptCardData } from '~/lib/data-contract/loyalty';

// ReceiptCardProps extends ReceiptCardData with component-specific props
export interface ReceiptCardProps extends ReceiptCardData {
    currency?: string;
    onClick?: (id: string) => void;
    className?: string;
}

const ReceiptsCard: React.FC<ReceiptCardProps> = ({
    id,
    storeName,
    date,
    totalAmount,
    currency = 'kr',
    productCount,
    pointsEarned,
    onClick,
    className,
}) => {
    const handleClick = () => {
        if (onClick) {
            onClick(id);
        }
    };

    return (
        <div
            className={`${styles.card} ${className || ''}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleClick();
                }
            }}
        >
            <div className={styles.dateContainer}>
                <div className={styles.day}>
                    <Typography variant="title-XS" weight="medium">
                        {date.day}.
                    </Typography>
                </div>
                <div className={styles.monthYear}>
                    <Typography variant="paragraph-XS" weight="regular">
                        {date.month}
                    </Typography>
                    <Typography variant="paragraph-XS" weight="regular">
                        {date.year}
                    </Typography>
                </div>
            </div>

            <div className={styles.contentContainer}>
                <div className={styles.storeInfo}>
                    <Typography
                        as="h3"
                        variant="paragraph-L"
                        weight="medium"
                        className={styles.storeName}
                    >
                        {storeName}
                    </Typography>
                    <div className={styles.totalAmount}>
                        <Typography variant="paragraph-S" weight="medium">
                            Total:&nbsp;
                        </Typography>
                        <Typography variant="paragraph-S" weight="regular">
                            {totalAmount} {currency}
                        </Typography>
                    </div>
                    <hr className={styles.separator} />
                    <div className={styles.details}>
                        <Typography as="p" variant="paragraph-XS" weight="regular">
                            {productCount} produkter
                        </Typography>
                        <Typography as="p" variant="paragraph-XS" weight="regular">
                            Optjent {pointsEarned >= 0 ? '+' : ''}
                            {pointsEarned} point
                        </Typography>
                    </div>
                </div>

                <div className={styles.chevronContainer}>
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={styles.chevron}
                        aria-hidden="true"
                    >
                        <path
                            d="M9 18L15 12L9 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default ReceiptsCard;
